// ============================================================================
// Activation thresholds (PRD §9, canonical) + Humulene from the infographic.
// Pure module — no I/O, no Supabase — so it is trivially unit-testable and
// shareable between the client and Edge Functions.
// ============================================================================

export type TerpeneSlug =
  | 'myrcene'
  | 'limonene'
  | 'linalool'
  | 'caryophyllene'
  | 'pinene'
  | 'humulene';

export const ACTIVATION_TEMP_C: Record<TerpeneSlug, number> = {
  myrcene: 167,
  limonene: 176,
  linalool: 155,
  caryophyllene: 160,
  pinene: 155,
  humulene: 198,
};

export type BatchProfile = Partial<Record<TerpeneSlug, number>>;

/**
 * Terpenes in the batch whose activation temperature is not reached.
 * Returned slugs can be surfaced as "Activation Alert" UI.
 */
export function dormantTerpenes(
  profile: BatchProfile,
  currentTempC: number,
  { minPercent = 0.001 }: { minPercent?: number } = {},
): TerpeneSlug[] {
  const out: TerpeneSlug[] = [];
  for (const [slug, pct] of Object.entries(profile) as [TerpeneSlug, number | undefined][]) {
    if ((pct ?? 0) < minPercent) continue;
    if (currentTempC < ACTIVATION_TEMP_C[slug]) out.push(slug);
  }
  return out;
}

/**
 * Recommended activation window for the dominant terpene(s) in a batch.
 * Returns a [min, max] range suitable for the TempDial heat map.
 */
export function recommendedWindow(profile: BatchProfile): { min: number; max: number } {
  const significant = (Object.entries(profile) as [TerpeneSlug, number | undefined][])
    .filter(([, pct]) => (pct ?? 0) >= 0.002)
    .sort(([, a], [, b]) => (b ?? 0) - (a ?? 0));

  if (significant.length === 0) return { min: 170, max: 190 };
  const topSlug = significant[0]![0];
  const base = ACTIVATION_TEMP_C[topSlug];
  return { min: base, max: Math.min(base + 25, 210) };
}
